import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Loader2, Cable, Search } from "lucide-react";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { TerminalType, UserTerminal } from "@/shared/types/userTerminalTypes";
import { useAddTerminalToServiceLine } from "@/domains/service-lines/services/account-service-lines";
import { useInfiniteAccountTerminals } from '@/domains/terminals/services/account-user-terminals';
import { ServiceLine } from '@/shared/types/serviceLineTypes';
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { RadioGroup, RadioGroupItem } from "@/shared/ui/radio-group";
import { showError, showErrorWithApiError, showSuccess } from "@/shared/utils/toast-utils";
import EmptyView from "@/shared/components/EmptyView";
import { DEBOUNCE_TIME } from "@/shared/utils/input-validator";
import SearchBar from "@/shared/components/SearchBar";
import { getTerminalTypeTextColorClass, TERMINAL_TYPE_INFO } from "@/shared/utils/color-utils-terminals";

interface AddTerminalToServiceLineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  serviceLine: ServiceLine;
}

const AddTerminalToServiceLineDialog = ({
  open,
  onOpenChange,
  onSuccess,
  serviceLine
}: AddTerminalToServiceLineDialogProps) => {
  const [selectedUserTerminal, setSelectedUserTerminal] = useState<UserTerminal | null>(null);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, DEBOUNCE_TIME);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const {
    data: userTerminalsInfiniteData,
    isLoading: isLoadingUserTerminals,
    isFetching: isFetchingUserTerminals,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteAccountTerminals(open, false, searchTerm);
  const addTerminalToServiceLine = useAddTerminalToServiceLine();

  const mergedUserTerminals = useMemo(() => {
    const pages = userTerminalsInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: UserTerminal[] = [];
    for (const page of pages) {
      for (const t of page.results ?? []) {
        if (!seen.has(t.userTerminalId)) {
          seen.add(t.userTerminalId);
          merged.push(t);
        }
      }
    }
    return merged;
  }, [userTerminalsInfiniteData?.pages]);

  const totalTerminalCount = userTerminalsInfiniteData?.pages[0]?.totalCount ?? 0;

  // service line has a product that supports L2VPN
  const hasL2VPNSupportedProduct = serviceLine.product?.l2vpnSupported === true;

  // Filter out user terminals that already have assigned to another service line and not a Starlink Mini type if the service line has a product that supports L2VPN
  const availableUserTerminals = useMemo(() => {
    return mergedUserTerminals.filter((userTerminal) =>
      !userTerminal.serviceLine?.serviceLineNumber &&
      (hasL2VPNSupportedProduct ? userTerminal.type !== TerminalType.STARLINK_MINI : true)
    );
  }, [mergedUserTerminals, hasL2VPNSupportedProduct]);

  // Filter terminals based on search term
  const filteredUserTerminals = useMemo(() => {
    const searchTermLower = searchTerm.toLowerCase();
    return availableUserTerminals.filter(userTerminal =>
      userTerminal.userTerminalId.toLowerCase().includes(searchTermLower) ||
      userTerminal.kitSerialNumber.toLowerCase().includes(searchTermLower) ||
      userTerminal.dishSerialNumber.toLowerCase().includes(searchTermLower)
    );
  }, [availableUserTerminals, searchTerm]);

  const isSearching = (searchInput.trim() !== searchTerm) || (Boolean(searchTerm) && isFetchingUserTerminals);

  useEffect(() => {
    if (!open) {
      setSelectedUserTerminal(null);
      setSearchInput('');
      setSearchTerm('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserTerminal) {
      showError(ERROR.SERVICE_LINE.SELECT_TERMINAL);
      return;
    }

    try {
      await
        addTerminalToServiceLine.mutateAsync({
          serviceLineNumber: serviceLine.serviceLineNumber,
          terminalId: selectedUserTerminal.userTerminalId
        })
      showSuccess(SUCCESS.SERVICE_LINE.ADDED_TERMINAL);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_LINE.ADD_TERMINAL);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent id="add-terminal-to-service-line-dialog" onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add Terminal to Service Line</DialogTitle>
          <DialogDescription>{serviceLine?.serviceLineName}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2 flex flex-col gap-2">
              {/* Search Input - only show when there are terminals */}
              <div className="relative">
                <SearchBar value={searchInput} onChange={setSearchInput} placeholder="Search for a terminal..." />
              </div>
              {isSearching && (
                <div className="flex items-center gap-2 text-xs">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Searching terminals...</span>
                </div>
              )}

              <p className="text-sm font-medium">Select Terminal</p>
              <ScrollArea className="h-[300px] w-full rounded-md border">
                <div className="p-4 space-y-3">
                  {isLoadingUserTerminals && mergedUserTerminals.length === 0 ? (
                    <div className="flex justify-center items-center min-h-[200px]">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : filteredUserTerminals.length > 0 ? (
                    <RadioGroup
                      value={selectedUserTerminal?.userTerminalId}
                      onValueChange={(value) => {
                        const userTerminal = filteredUserTerminals.find((ut) => ut.userTerminalId === value);
                        setSelectedUserTerminal(userTerminal || null);
                      }}
                      className="space-y-2"
                    >
                      {filteredUserTerminals.map((userTerminal) => (
                        <div key={userTerminal.userTerminalId} className="flex items-center space-x-2">
                          <RadioGroupItem
                            value={userTerminal.userTerminalId}
                            id={`userTerminal-${userTerminal.userTerminalId}`}
                          />
                          <Label
                            htmlFor={`userTerminal-${userTerminal.userTerminalId}`}
                            className="text-sm font-normal flex-1"
                          >
                            <div className="flex flex-col">
                              <span>{userTerminal.userTerminalId}</span>
                              <span className="text-xs">
                                Kit: {userTerminal.kitSerialNumber} | Serial: {userTerminal.dishSerialNumber}
                              </span>
                              {userTerminal.type && (
                                <span className={`text-xs ${getTerminalTypeTextColorClass(userTerminal.type)}`}>{TERMINAL_TYPE_INFO[userTerminal.type].value}</span>
                              )}
                            </div>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  ) : searchTerm ? (
                    <EmptyView
                      icon={Search}
                      size="small"
                      title="No Terminals Found"
                      description={`No terminals match "${searchTerm}". Try adjusting your search.`}
                    />
                  ) : hasNextPage ? (
                    <EmptyView
                      icon={Cable}
                      size="small"
                      title="No Unassigned Terminals Yet"
                      description="No unassigned terminals on the pages loaded so far. Load more to search further."
                    />
                  ) : mergedUserTerminals.length > 0 ? (
                    <EmptyView
                      icon={Cable}
                      size="small"
                      title="No Unassigned Terminals"
                      description="All terminals loaded for this account are already assigned to a service line."
                    />
                  ) : (
                    <EmptyView
                      icon={Cable}
                      size="small"
                      title="No User Terminals Available"
                      description="There are no user terminals available to add to this service line."
                    />
                  )}
                  {(hasNextPage || (totalTerminalCount > 0 && mergedUserTerminals.length > 0)) && (
                    <div className="glass-panel flex flex-col gap-2 border-t p-2 rounded-lg">
                      {totalTerminalCount > 0 && (
                        <p className="text-xs">
                          Loaded {mergedUserTerminals.length} of {totalTerminalCount} terminals
                          {availableUserTerminals.length < mergedUserTerminals.length && (
                            <span> {availableUserTerminals.length} unassigned terminals</span>
                          )}
                        </p>
                      )}
                      {hasNextPage ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full gap-2"
                          disabled={isFetchingNextPage}
                          onClick={() => void fetchNextPage()}
                        >
                          {isFetchingNextPage ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading…
                            </>
                          ) : (
                            "Load more"
                          )}
                        </Button>
                      ) : null}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => onOpenChange(false)}
              disabled={addTerminalToServiceLine.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={addTerminalToServiceLine.isPending || !selectedUserTerminal}
              className="gap-2"
            >
              {addTerminalToServiceLine.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add to Selected Terminal to Service Line'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTerminalToServiceLineDialog; 